import type { CorpusOverview as Overview, ValueCount } from "@noa/api-core";
import { useTranslation } from "react-i18next";
import { Card, cn, Tag, Text } from "~/components/ui";
import { chunkKindKey, sourceLabelKey } from "~/utils/corpus-admin";

interface CorpusOverviewProps {
  overview: Overview | undefined;
  isLoading: boolean;
  /** The chunks and the lexicon too: what an admin diagnoses, not what a user looks for. */
  detailed: boolean;
}

interface BreakdownProps {
  title: string;
  counts: ValueCount[];
  label: (value: string) => string;
  formatNumber: (value: number) => string;
}

function Breakdown({ title, counts, label, formatNumber }: BreakdownProps) {
  return (
    <Card size="small" title={title}>
      <div className="flex flex-wrap gap-2">
        {counts.map((count) => (
          <Tag key={count.value}>
            {label(count.value)} <Text strong>{formatNumber(count.count)}</Text>
          </Tag>
        ))}
      </div>
    </Card>
  );
}

/** Collection sizes, then how documents (and, detailed, chunks) break down. */
export function CorpusOverview({ overview, isLoading, detailed }: CorpusOverviewProps) {
  const { t, i18n } = useTranslation("admin");
  const formatNumber = (value: number) => value.toLocaleString(i18n.language);

  const stats = [
    { key: "documents", value: overview?.documents },
    ...(detailed
      ? [
          { key: "chunks", value: overview?.chunks },
          { key: "lexicon", value: overview?.lexicon_concepts },
        ]
      : []),
  ];

  const documentsByType = overview
    ? [
        ...overview.documents_by_type,
        ...(overview.untyped_documents > 0 ? [{ value: "", count: overview.untyped_documents }] : []),
      ]
    : [];
  // Plain text chunks carry no source type: the API counts them apart
  const chunksByKind = overview
    ? [...overview.chunks_by_source_type, { value: "text", count: overview.chunks_without_source_type }].filter(
        (count) => count.count > 0
      )
    : [];

  return (
    <div className="mb-4 flex flex-col gap-3">
      <div className={cn("grid grid-cols-1 gap-3", detailed && "sm:grid-cols-3")}>
        {stats.map((stat) => (
          <Card key={stat.key} size="small" loading={isLoading}>
            <Text tone="muted">{t(`vectorstoreAdmin.overview.${stat.key}`)}</Text>
            <div className="text-2xl font-semibold text-noa">
              {stat.value === undefined ? "—" : formatNumber(stat.value)}
            </div>
          </Card>
        ))}
      </div>
      {overview && (
        <div className={cn("grid grid-cols-1 gap-3", detailed ? "lg:grid-cols-3" : "lg:grid-cols-2")}>
          <Breakdown
            title={t("vectorstoreAdmin.overview.byType")}
            counts={documentsByType}
            label={(value) => value || t("vectorstoreAdmin.overview.untyped")}
            formatNumber={formatNumber}
          />
          <Breakdown
            title={t("vectorstoreAdmin.overview.bySource")}
            counts={overview.documents_by_source}
            label={(value) => t(sourceLabelKey(value), { defaultValue: value })}
            formatNumber={formatNumber}
          />
          {detailed && (
            <Breakdown
              title={t("vectorstoreAdmin.overview.byChunkKind")}
              counts={chunksByKind}
              label={(value) => t(chunkKindKey(value), { defaultValue: value })}
              formatNumber={formatNumber}
            />
          )}
        </div>
      )}
    </div>
  );
}
